import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { BrandingField, defaultBrandingSettings } from '@app/core/models/branding.models';
import { AppFeature } from '@app/core/models/config.models';
import { AppConfigService } from '@app/core/services/app-config.service';
import { BrandingService } from '@app/core/services/branding.service';
import { BrandingColorDirective } from '@app/shared/directives/branding-color.directive';
import { PhoneNumberPipe } from '@app/shared/pipes/phone-number.pipe';
import { validatePhoneNumber } from '@app/shared/utils/phone.util';
import { TranslateModule } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

@Component({
  standalone: true,
  imports: [
    MatIconModule,
    CommonModule,
    BrandingColorDirective,
    ReactiveFormsModule,
    TranslateModule,
    PhoneNumberPipe,
    MatDividerModule,
    MatFormFieldModule,
    MatButtonModule,
    MatInputModule,
    MatCheckboxModule,
    FormsModule,
  ],
  selector: 'app-blocked-numbers',
  templateUrl: './blocked-numbers.component.html',
  styleUrls: ['./blocked-numbers.component.scss'],
})
export class BlockedNumbersComponent implements OnInit, AfterViewInit {
  @ViewChild('container') container: ElementRef<HTMLElement>;
  @ViewChild('greyMask') greyMask?: ElementRef<HTMLDivElement>;

  protected editingDisabled = true;
  protected readonly BrandingField = BrandingField;
  protected blockedNumbers: string[] = [];
  protected allowedNumbers: string[] = [];
  protected formGroup: FormGroup;
  protected defaultBrandingSettings = defaultBrandingSettings;

  constructor(
    private appConfigService: AppConfigService,
    protected brandingService: BrandingService,
    private fb: FormBuilder
  ) {}

  async ngOnInit() {
    this.editingDisabled = !this.appConfigService.features[AppFeature.ModifyBlockedNumbers];

    this.createFormGroup();
    await this.getBlockedNumbers();
    await this.getAllowedNumbers();
    await this.getPreferences();
  }

  ngAfterViewInit(): void {
    if (this.editingDisabled) {
      // pass scroll events from grey mask to main container
      this.greyMask?.nativeElement.addEventListener('wheel', (event: WheelEvent) => {
        this.container.nativeElement.scrollBy(event.deltaX, event.deltaY);
      });

      // block mouse and keyboard events
      this.container.nativeElement.addEventListener('click', this.stopAndPreventDefault, true);
      this.container.nativeElement.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key !== 'Escape') {
          this.stopAndPreventDefault(event);
        }
      });
    }
  }

  private stopAndPreventDefault(event: Event) {
    event.stopPropagation();
    event.preventDefault();
  }

  private createFormGroup() {
    this.formGroup = this.fb.group({
      blockedNumber: ['', [Validators.required, validatePhoneNumber()]],
      allowedNumber: ['', [Validators.required, validatePhoneNumber(true)]],
      blockAnonymous: [false],
    });
  }

  private async getBlockedNumbers() {
    const response = await firstValueFrom(this.appConfigService.getBlockedNumbers());
    this.blockedNumbers = response.numbers;
  }

  private async getAllowedNumbers() {
    const response = await firstValueFrom(this.appConfigService.getAllowedNumbers());
    this.allowedNumbers = response.numbers;
  }

  private async getPreferences() {
    const response = await firstValueFrom(this.appConfigService.getPreferences());
    this.formGroup.controls['blockAnonymous'].patchValue(response.blockAnonymous || false);
  }

  async addBlockedNumbers() {
    if (this.formGroup.controls['blockedNumber'].invalid) {
      return;
    }
    await firstValueFrom(this.appConfigService.setBlockedNumbers(this.formGroup.controls['blockedNumber'].value));
    this.blockedNumbers.push(this.formGroup.controls['blockedNumber'].value);
    this.formGroup.controls['blockedNumber'].patchValue('');
    this.formGroup.controls['blockedNumber'].setErrors(null);
  }

  async removeBlockedNumber(number: string) {
    await firstValueFrom(this.appConfigService.deleteBlockedNumbers(number));
    this.blockedNumbers = this.blockedNumbers.filter((item) => item !== number);
  }

  async addAllowedNumbers() {
    if (this.formGroup.controls['allowedNumber'].invalid) {
      return;
    }
    await firstValueFrom(this.appConfigService.setAllowedNumbers(this.formGroup.controls['allowedNumber'].value));
    this.allowedNumbers.push(this.formGroup.controls['allowedNumber'].value);
    this.formGroup.controls['allowedNumber'].patchValue('');
    this.formGroup.controls['allowedNumber'].setErrors(null);
  }

  async removeAllowedNumber(number: string) {
    await firstValueFrom(this.appConfigService.deleteAllowedNumbers(number));
    this.allowedNumbers = this.allowedNumbers.filter((item) => item !== number);
  }

  async onCheckChangeBlockAnonymous() {
    await firstValueFrom(
      this.appConfigService.updatePreferences({ blockAnonymous: this.formGroup.controls['blockAnonymous'].value })
    );
  }
}
